import React, { useState, useEffect, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// XMLHttpRequest helper to bypass the global fetch interceptor that consumes
// response body streams before they can be read. Same pattern proven in
// authService.js and MIQAnticipationBanner.js.
const xhrRequest = (url, { responseType = 'json', timeoutMs = 25000 } = {}) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    if (responseType === 'blob') xhr.responseType = 'blob';
    xhr.timeout = timeoutMs;
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        // Capture body so caller can surface backend's `detail` in the UI
        let detail = '';
        try { detail = JSON.parse(xhr.responseText)?.detail || ''; } catch (_) { detail = xhr.responseText?.slice(0, 120) || ''; }
        const err = new Error(`HTTP ${xhr.status}${detail ? ' — ' + detail : ''}`);
        err.status = xhr.status;
        err.detail = detail;
        return reject(err);
      }
      if (responseType === 'blob') {
        const headers = xhr.getAllResponseHeaders();
        return resolve({ blob: xhr.response, headers });
      }
      try {
        return resolve(JSON.parse(xhr.responseText));
      } catch (e) {
        return reject(new Error('Invalid JSON response'));
      }
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.ontimeout = () => reject(new Error('Request timed out'));
    xhr.send();
  });

const AdminDashboard = ({ userId, colors, spacing, radius, typography }) => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [promoStats, setPromoStats] = useState(null);
  const [trialConversions, setTrialConversions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  // Diagnostic: collect per-endpoint errors so the empty-state UI can surface
  // the actual HTTP status / detail (catches env-var drift between preview
  // and production deploys, service-worker cache issues, etc.)
  const [loadErrors, setLoadErrors] = useState({});

  const fetchStats = useCallback(async () => {
    try {
      const data = await xhrRequest(`${BACKEND_URL}/api/admin/dashboard-stats?user_id=${userId}`);
      setStats(data);
      setLoadErrors(prev => { const { stats: _, ...rest } = prev; return rest; });
    } catch (e) {
      console.error('Failed to load admin stats:', e);
      setLoadErrors(prev => ({ ...prev, stats: e.message || String(e) }));
    }
  }, [userId]);

  const fetchUsers = useCallback(async (page = 1, search = '') => {
    try {
      const params = new URLSearchParams({ user_id: userId, page, per_page: 25 });
      if (search) params.append('search', search);
      const data = await xhrRequest(`${BACKEND_URL}/api/admin/users?${params}`);
      setUsers(data.users);
      setTotalPages(data.total_pages);
      setTotalUsers(data.total);
      setCurrentPage(data.page);
      setLoadErrors(prev => { const { users: _, ...rest } = prev; return rest; });
    } catch (e) {
      console.error('Failed to load users:', e);
      setLoadErrors(prev => ({ ...prev, users: e.message || String(e) }));
    }
  }, [userId]);

  const fetchPromoStats = useCallback(async () => {
    try {
      const data = await xhrRequest(`${BACKEND_URL}/api/admin/promo-stats?user_id=${userId}`);
      setPromoStats(data);
    } catch (e) {
      console.error('Failed to load promo stats:', e);
      setLoadErrors(prev => ({ ...prev, promo: e.message || String(e) }));
    }
  }, [userId]);

  const fetchTrialConversions = useCallback(async () => {
    try {
      const data = await xhrRequest(`${BACKEND_URL}/api/admin/trial-conversions?user_id=${userId}`);
      setTrialConversions(data);
    } catch (e) {
      console.error('Failed to load trial conversions:', e);
      setLoadErrors(prev => ({ ...prev, trials: e.message || String(e) }));
    }
  }, [userId]);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      try {
        // Load with individual error handling - don't let one failure block all
        await Promise.allSettled([
          fetchStats(),
          fetchUsers(1),
          fetchPromoStats(),
          fetchTrialConversions()
        ]);
      } catch (e) {
        console.error('Admin dashboard load error:', e);
      }
      setLoading(false);
    };
    loadAll();
  }, [fetchStats, fetchUsers, fetchPromoStats, fetchTrialConversions]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(1, searchQuery);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return '—'; }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return 'Never';
    try {
      const d = new Date(dateStr);
      const now = new Date();
      const diffMs = now - d;
      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHrs = Math.floor(diffMins / 60);
      if (diffHrs < 24) return `${diffHrs}h ago`;
      const diffDays = Math.floor(diffHrs / 24);
      if (diffDays < 30) return `${diffDays}d ago`;
      return formatDate(dateStr);
    } catch { return '—'; }
  };

  // Styles
  const cardStyle = {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: radius?.lg || '12px',
    padding: spacing?.s4 || '16px',
  };

  const statCardStyle = {
    ...cardStyle,
    textAlign: 'center',
    flex: '1 1 140px',
    minWidth: '120px',
  };

  const tabStyle = (isActive) => ({
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: isActive ? '600' : '400',
    background: isActive ? 'rgba(99, 102, 241, 0.2)' : 'transparent',
    color: isActive ? '#818cf8' : 'rgba(255,255,255,0.5)',
    transition: 'all 0.2s',
  });

  const badgeStyle = (type) => {
    const map = {
      active: { bg: 'rgba(34,197,94,0.15)', color: '#22c55e', text: 'Active' },
      auto_active: { bg: 'rgba(59,130,246,0.15)', color: '#3b82f6', text: 'Auto Trial' },
      expired: { bg: 'rgba(239,68,68,0.15)', color: '#ef4444', text: 'Expired' },
      none: { bg: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)', text: 'No Trial' },
      pending: { bg: 'rgba(251,191,36,0.15)', color: '#fbbf24', text: 'Not Signed Up' },
      unknown: { bg: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.3)', text: '—' },
    };
    const m = map[type] || map.unknown;
    return {
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: '600',
      background: m.bg,
      color: m.color,
    };
  };

  const getBadgeText = (type) => {
    const map = { active: 'Active', auto_active: 'Auto Trial', expired: 'Expired', none: 'No Trial', pending: 'Not Signed Up' };
    return map[type] || '—';
  };

  const [exporting, setExporting] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleExport = async (exportType) => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ user_id: userId, export_type: exportType });
      if (searchQuery) params.append('search', searchQuery);
      const { blob, headers } = await xhrRequest(
        `${BACKEND_URL}/api/admin/export-users?${params}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const dispositionMatch = headers.match(/content-disposition:.*filename=(.+?)(?:\r|\n|$)/i);
      a.download = dispositionMatch ? dispositionMatch[1].trim() : `users_${exportType}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed:', e);
    }
    setExporting(false);
  };

  const handleCopyEmails = async () => {
    try {
      const params = new URLSearchParams({ user_id: userId });
      if (searchQuery) params.append('search', searchQuery);
      const data = await xhrRequest(`${BACKEND_URL}/api/admin/copy-emails?${params}`);
      await navigator.clipboard.writeText(data.emails.join('\n'));
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2500);
    } catch (e) {
      console.error('Copy emails failed:', e);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px 16px', textAlign: 'center' }}>
        <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}>Loading admin dashboard...</div>
      </div>
    );
  }

  if (!stats && users.length === 0) {
    const errorEntries = Object.entries(loadErrors);
    return (
      <div data-testid="admin-dashboard-unavailable" style={{ padding: '40px 16px', textAlign: 'center', maxWidth: '500px', margin: '0 auto' }}>
        <div style={{ fontSize: '16px', fontWeight: '600', color: '#ef4444', marginBottom: '8px' }}>Dashboard Unavailable</div>
        <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginBottom: '16px' }}>
          Could not load admin data. Diagnostics below.
        </div>
        {errorEntries.length > 0 && (
          <div style={{
            textAlign: 'left',
            background: 'rgba(239,68,68,0.06)',
            border: '1px solid rgba(239,68,68,0.18)',
            borderRadius: '8px',
            padding: '10px 12px',
            marginBottom: '16px',
            fontSize: '11px',
            color: 'rgba(255,255,255,0.7)',
            fontFamily: 'monospace',
            wordBreak: 'break-word',
          }}>
            {errorEntries.map(([endpoint, msg]) => (
              <div key={endpoint} style={{ marginBottom: '4px' }}>
                <span style={{ color: '#ef4444', fontWeight: '600' }}>{endpoint}:</span> {msg}
              </div>
            ))}
            <div style={{ marginTop: '8px', fontSize: '10px', color: 'rgba(255,255,255,0.4)' }}>
              user_id: {userId || '(none)'} · backend: {BACKEND_URL}
            </div>
          </div>
        )}
        <button
          data-testid="admin-retry-btn"
          onClick={() => { setLoading(true); setLoadErrors({}); Promise.allSettled([fetchStats(), fetchUsers(1), fetchPromoStats(), fetchTrialConversions()]).then(() => setLoading(false)); }}
          style={{ padding: '8px 20px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', color: '#ef4444', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" style={{ padding: '0 16px 80px', maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#fff', margin: '0 0 4px' }}>Admin Dashboard</h1>
        <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', margin: 0 }}>SmartPlAIte Analytics & User Management</p>
      </div>

      {/* Tabs */}
      <div data-testid="admin-tabs" style={{ display: 'flex', gap: '4px', marginBottom: '20px', overflowX: 'auto', paddingBottom: '4px' }}>
        {['overview', 'users', 'promo', 'trials'].map(tab => (
          <button
            key={tab}
            data-testid={`admin-tab-${tab}`}
            onClick={() => setActiveTab(tab)}
            style={tabStyle(activeTab === tab)}
          >
            {tab === 'overview' ? 'Overview' : tab === 'users' ? 'Users' : tab === 'promo' ? 'Promo Codes' : 'Trial Funnel'}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && stats && (
        <div data-testid="admin-overview">
          {/* User Stats Row */}
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ fontSize: '13px', fontWeight: '600', color: 'rgba(255,255,255,0.5)', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Testers</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              <div style={statCardStyle}>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#818cf8' }}>{stats.overview.total_testers}</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>Invited</div>
              </div>
              <div style={statCardStyle}>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#22c55e' }}>{stats.overview.signed_up}</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>Signed Up</div>
              </div>
              <div style={statCardStyle}>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#fbbf24' }}>{stats.overview.pending_signup}</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>Pending</div>
              </div>
              <div style={statCardStyle}>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#f59e0b' }}>{stats.engagement.active_users_7d}</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>Active (7d)</div>
              </div>
            </div>
          </div>

          {/* Auth Provider Breakdown */}
          <div style={{ ...cardStyle, marginBottom: '16px' }}>
            <h3 style={{ fontSize: '13px', fontWeight: '600', color: 'rgba(255,255,255,0.5)', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Auth Providers</h3>
            <div style={{ display: 'flex', gap: '20px' }}>
              <div>
                <span style={{ fontSize: '20px', fontWeight: '700', color: '#fff' }}>{stats.overview.email_users}</span>
                <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginLeft: '6px' }}>Email</span>
              </div>
              <div>
                <span style={{ fontSize: '20px', fontWeight: '700', color: '#fff' }}>{stats.overview.google_users}</span>
                <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginLeft: '6px' }}>Google</span>
              </div>
            </div>
          </div>

          {/* Trial Stats */}
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ fontSize: '13px', fontWeight: '600', color: 'rgba(255,255,255,0.5)', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Trials</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              <div style={statCardStyle}>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#22c55e' }}>{stats.trials.active_trials}</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>Active Trials</div>
              </div>
              <div style={statCardStyle}>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#f59e0b' }}>{stats.trials.promo_trials}</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>Promo Trials</div>
              </div>
              <div style={statCardStyle}>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#3b82f6' }}>{stats.trials.standard_trials}</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>Standard Trials</div>
              </div>
            </div>
          </div>

          {/* Promo Quick Stats */}
          <div style={{ ...cardStyle, marginBottom: '16px' }}>
            <h3 style={{ fontSize: '13px', fontWeight: '600', color: 'rgba(255,255,255,0.5)', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Promo Codes</h3>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#818cf8', marginBottom: '10px' }}>{stats.promo.total_redemptions} <span style={{ fontSize: '13px', fontWeight: '400', color: 'rgba(255,255,255,0.4)' }}>total redemptions</span></div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {Object.entries(stats.promo.by_code).map(([code, count]) => (
                <div key={code} style={{
                  background: count > 0 ? 'rgba(99,102,241,0.12)' : 'rgba(255,255,255,0.04)',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}>
                  <span style={{ fontWeight: '600', color: count > 0 ? '#818cf8' : 'rgba(255,255,255,0.4)' }}>{code}</span>
                  <span style={{ color: 'rgba(255,255,255,0.5)', marginLeft: '6px' }}>{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Engagement Stats */}
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ fontSize: '13px', fontWeight: '600', color: 'rgba(255,255,255,0.5)', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Engagement</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {[
                { val: stats.engagement.total_meals_logged, label: 'Total Meals', color: '#22c55e' },
                { val: stats.engagement.meals_last_7d, label: 'Meals (7d)', color: '#3b82f6' },
                { val: stats.engagement.meals_last_30d, label: 'Meals (30d)', color: '#8b5cf6' },
                { val: stats.engagement.unique_users_with_meals, label: 'Users w/ Meals', color: '#f59e0b' },
                { val: stats.engagement.smart_goals_completed, label: 'Smart Goals', color: '#ec4899' },
                { val: stats.engagement.total_checkins, label: 'Check-ins', color: '#06b6d4' },
                { val: stats.engagement.total_workout_plans, label: 'Workout Plans', color: '#ef4444' },
                { val: stats.engagement.total_exercises_logged, label: 'Exercises', color: '#84cc16' },
                { val: stats.engagement.health_sync_users || 0, label: 'Health Sync (ever)', color: '#00E5A0' },
                { val: stats.engagement.health_sync_users_7d || 0, label: 'Health Sync (7d)', color: '#10b981' },
              ].map(item => (
                <div key={item.label} style={statCardStyle}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: item.color }}>{item.val}</div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div data-testid="admin-users">
          {/* Search */}
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <input
              data-testid="admin-user-search"
              type="text"
              placeholder="Search by email or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                flex: 1,
                padding: '10px 14px',
                borderRadius: '10px',
                border: '1px solid rgba(255,255,255,0.1)',
                background: 'rgba(255,255,255,0.04)',
                color: '#fff',
                fontSize: '13px',
                outline: 'none',
              }}
            />
            <button
              data-testid="admin-user-search-btn"
              type="submit"
              style={{
                padding: '10px 18px',
                borderRadius: '10px',
                border: 'none',
                background: '#818cf8',
                color: '#fff',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >Search</button>
          </form>

          {/* Export & Copy Actions */}
          <div data-testid="admin-export-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
            <button
              data-testid="admin-export-basic"
              onClick={() => handleExport('basic')}
              disabled={exporting}
              style={{
                padding: '7px 14px', borderRadius: '8px', border: '1px solid rgba(34,197,94,0.3)',
                background: 'rgba(34,197,94,0.1)', color: '#22c55e', fontSize: '12px', fontWeight: '600',
                cursor: exporting ? 'default' : 'pointer', opacity: exporting ? 0.5 : 1,
              }}
            >
              {exporting ? 'Exporting...' : 'Export Basic CSV'}
            </button>
            <button
              data-testid="admin-export-full"
              onClick={() => handleExport('full')}
              disabled={exporting}
              style={{
                padding: '7px 14px', borderRadius: '8px', border: '1px solid rgba(99,102,241,0.3)',
                background: 'rgba(99,102,241,0.1)', color: '#818cf8', fontSize: '12px', fontWeight: '600',
                cursor: exporting ? 'default' : 'pointer', opacity: exporting ? 0.5 : 1,
              }}
            >
              {exporting ? 'Exporting...' : 'Export Full CSV'}
            </button>
            <button
              data-testid="admin-copy-emails"
              onClick={handleCopyEmails}
              style={{
                padding: '7px 14px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)',
                background: copySuccess ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.04)',
                color: copySuccess ? '#22c55e' : 'rgba(255,255,255,0.6)',
                fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {copySuccess ? 'Copied!' : 'Copy All Emails'}
            </button>
            {searchQuery && (
              <span style={{ display: 'flex', alignItems: 'center', fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>
                Filtered: "{searchQuery}"
              </span>
            )}
          </div>

          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginBottom: '10px' }}>
            {totalUsers} testers — <span style={{ color: '#22c55e' }}>{users.filter(u => u.signed_up).length} signed up</span> · <span style={{ color: '#fbbf24' }}>{users.filter(u => !u.signed_up).length} pending</span> — Page {currentPage} of {totalPages}
          </div>

          {/* User List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {users.map(u => (
              <div key={u.email} data-testid={`admin-user-row-${u.user_id || u.email}`} style={{
                ...cardStyle,
                padding: '12px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                opacity: u.signed_up === false ? 0.55 : 1,
                borderColor: u.signed_up === false ? 'rgba(251,191,36,0.15)' : undefined,
              }}>
                {/* Row 1: Email + Provider badge */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#fff', wordBreak: 'break-all', flex: '1 1 auto', minWidth: 0 }}>{u.email}</div>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexShrink: 0 }}>
                    {u.last_health_sync && (
                      <span
                        title={`Health Sync: ${u.health_sync_source || 'connected'} · last ${formatTimeAgo(u.last_health_sync)}`}
                        style={{
                          fontSize: '10px', padding: '2px 6px', borderRadius: '4px',
                          background: 'rgba(0,229,160,0.12)', color: '#00E5A0', fontWeight: '600',
                          letterSpacing: '0.02em',
                        }}
                      >
                        {(u.health_sync_source === 'apple_healthkit' ? 'HK' : u.health_sync_source === 'google_fit' ? 'GF' : '♥')} · {formatTimeAgo(u.last_health_sync)}
                      </span>
                    )}
                    {u.signed_up === false ? (
                      <span style={{
                        fontSize: '10px', padding: '2px 6px', borderRadius: '4px',
                        background: 'rgba(251,191,36,0.15)', color: '#fbbf24', fontWeight: '600',
                      }}>invited</span>
                    ) : (
                      <span style={{
                        fontSize: '10px', padding: '2px 6px', borderRadius: '4px',
                        background: u.auth_provider === 'google' ? 'rgba(66,133,244,0.2)' : 'rgba(255,255,255,0.06)',
                        color: u.auth_provider === 'google' ? '#4285f4' : 'rgba(255,255,255,0.5)',
                        fontWeight: '600',
                      }}>{u.auth_provider}</span>
                    )}
                  </div>
                </div>

                {/* Row 2: Name + Signup date */}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
                  <span>{u.name || (u.signed_up === false ? 'Not yet signed up' : 'No name')}</span>
                  <span>{u.signed_up === false ? '' : `Joined ${formatDate(u.created_at)}`}</span>
                </div>

                {/* Row 3: Trial + Promo + Engagement */}
                {u.signed_up !== false && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                    <span style={badgeStyle(u.trial_status)}>{getBadgeText(u.trial_status)}</span>
                    {u.trial_type === 'promo' && (
                      <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', background: 'rgba(168,85,247,0.15)', color: '#a855f7', fontWeight: '600' }}>
                        Promo: {u.promo_code_used}
                      </span>
                    )}
                    <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', marginLeft: 'auto' }}>
                      {u.meals_logged} meals · {u.checkins} check-ins · {u.has_smart_goals ? 'Goals set' : 'No goals'} · Last: {formatTimeAgo(u.last_active)}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
              <button
                data-testid="admin-users-prev"
                disabled={currentPage <= 1}
                onClick={() => fetchUsers(currentPage - 1, searchQuery)}
                style={{
                  padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: currentPage <= 1 ? 'default' : 'pointer',
                  background: currentPage <= 1 ? 'rgba(255,255,255,0.04)' : 'rgba(99,102,241,0.2)',
                  color: currentPage <= 1 ? 'rgba(255,255,255,0.2)' : '#818cf8', fontSize: '13px', fontWeight: '600',
                }}
              >Prev</button>
              <span style={{ display: 'flex', alignItems: 'center', fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
                {currentPage} / {totalPages}
              </span>
              <button
                data-testid="admin-users-next"
                disabled={currentPage >= totalPages}
                onClick={() => fetchUsers(currentPage + 1, searchQuery)}
                style={{
                  padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: currentPage >= totalPages ? 'default' : 'pointer',
                  background: currentPage >= totalPages ? 'rgba(255,255,255,0.04)' : 'rgba(99,102,241,0.2)',
                  color: currentPage >= totalPages ? 'rgba(255,255,255,0.2)' : '#818cf8', fontSize: '13px', fontWeight: '600',
                }}
              >Next</button>
            </div>
          )}
        </div>
      )}

      {/* Promo Codes Tab */}
      {activeTab === 'promo' && promoStats && (
        <div data-testid="admin-promo">
          {/* Code Stats */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '13px', fontWeight: '600', color: 'rgba(255,255,255,0.5)', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Code Performance ({promoStats.total_redemptions} total)
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {promoStats.codes.map(c => {
                const maxRedemptions = Math.max(...promoStats.codes.map(x => x.redemptions), 1);
                const pct = (c.redemptions / maxRedemptions) * 100;
                return (
                  <div key={c.code} style={{ ...cardStyle, padding: '12px 14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '700', color: '#818cf8' }}>{c.code}</span>
                      <span style={{ fontSize: '14px', fontWeight: '700', color: '#fff' }}>{c.redemptions}</span>
                    </div>
                    <div style={{ height: '4px', borderRadius: '2px', background: 'rgba(255,255,255,0.06)' }}>
                      <div style={{ height: '100%', borderRadius: '2px', background: '#818cf8', width: `${pct}%`, transition: 'width 0.5s ease' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recent Redemptions */}
          <h3 style={{ fontSize: '13px', fontWeight: '600', color: 'rgba(255,255,255,0.5)', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Recent Redemptions</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {promoStats.recent_redemptions.length === 0 ? (
              <div style={{ ...cardStyle, textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '13px' }}>No redemptions yet</div>
            ) : (
              promoStats.recent_redemptions.map((r, i) => (
                <div key={i} style={{ ...cardStyle, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#fff', fontWeight: '500' }}>{r.email || r.user_id}</div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>{formatDate(r.redeemed_at)}</div>
                  </div>
                  <span style={{
                    padding: '3px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600',
                    background: 'rgba(168,85,247,0.15)', color: '#a855f7',
                  }}>{r.promo_code}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Trial Funnel Tab */}
      {activeTab === 'trials' && trialConversions && (
        <div data-testid="admin-trials">
          {/* Funnel */}
          <h3 style={{ fontSize: '13px', fontWeight: '600', color: 'rgba(255,255,255,0.5)', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Conversion Funnel</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '20px' }}>
            {[
              { label: 'Total Signups', val: trialConversions.funnel.total_signups, color: '#818cf8', pct: 100 },
              { label: 'Auto Trial Only (7-day)', val: trialConversions.funnel.auto_trial_only, color: '#3b82f6', pct: Math.round((trialConversions.funnel.auto_trial_only / Math.max(trialConversions.funnel.total_signups, 1)) * 100) },
              { label: 'Explicit Trials Started', val: trialConversions.funnel.explicit_trials_started, color: '#22c55e', pct: Math.round((trialConversions.funnel.explicit_trials_started / Math.max(trialConversions.funnel.total_signups, 1)) * 100) },
              { label: 'Active Trials', val: trialConversions.funnel.active_trials, color: '#f59e0b', pct: Math.round((trialConversions.funnel.active_trials / Math.max(trialConversions.funnel.total_signups, 1)) * 100) },
              { label: 'Expired Trials', val: trialConversions.funnel.expired_trials, color: '#ef4444', pct: Math.round((trialConversions.funnel.expired_trials / Math.max(trialConversions.funnel.total_signups, 1)) * 100) },
            ].map(item => (
              <div key={item.label} style={{ ...cardStyle, padding: '10px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>{item.label}</span>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: item.color }}>{item.val} <span style={{ fontSize: '10px', fontWeight: '400', color: 'rgba(255,255,255,0.3)' }}>({item.pct}%)</span></span>
                </div>
                <div style={{ height: '3px', borderRadius: '2px', background: 'rgba(255,255,255,0.06)' }}>
                  <div style={{ height: '100%', borderRadius: '2px', background: item.color, width: `${item.pct}%`, transition: 'width 0.5s ease' }} />
                </div>
              </div>
            ))}
          </div>

          {/* Breakdown */}
          <h3 style={{ fontSize: '13px', fontWeight: '600', color: 'rgba(255,255,255,0.5)', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Trial Type Breakdown</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {[
              { label: 'Standard Active', val: trialConversions.breakdown.standard_active, color: '#3b82f6' },
              { label: 'Standard Expired', val: trialConversions.breakdown.standard_expired, color: '#94a3b8' },
              { label: 'Promo Active', val: trialConversions.breakdown.promo_active, color: '#a855f7' },
              { label: 'Promo Expired', val: trialConversions.breakdown.promo_expired, color: '#6b7280' },
            ].map(item => (
              <div key={item.label} style={statCardStyle}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: item.color }}>{item.val}</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
